/**
 * US-006 — Admin-Panel „Externer Kundenlink" im PB-Stundenplan-Tab.
 *
 * Erzeugt/verwaltet den öffentlichen, PIN-geschützten Link, über den der Kunde
 * den Stundenplan ohne Account ausfüllt. Zeigt Status (offen / festgelegt),
 * Link + PIN (kopierbar) und Aktionen (neu erzeugen, widerrufen, wieder
 * freigeben). Meldet Sperr-Änderungen via `onLockChange` an den Tab, damit
 * dieser den Speichern-Button entsprechend sperrt.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Card } from '../../../components/ui';
import { getThemeColors } from '../../../lib/theme';
import { usePermission } from '../../../contexts/PermissionsContext';
import {
  getPbScheduleLink,
  createPbScheduleLink,
  deletePbScheduleLink,
  unlockPbSchedule,
  sendPbScheduleLink,
  type PbScheduleLinkInfo,
} from '../../../lib/api';
import { v1Contacts, type V1Contact } from '../../../lib/v1';
import { v2FamilyAccounts } from '../../../lib/familyAccounts';
import i18n from '../../../i18n';
import { formatDateTime } from '../../../lib/dateFormat';

/** Anzeigename eines Ansprechpartners. */
function contactName(c: V1Contact, tr: (key: string, opts?: Record<string, unknown>) => string = (k, opts) => i18n.t(k, opts)): string {
  return (c.fullname || `${c.vorname ?? ''} ${c.nachname ?? ''}`.trim() || c.email || tr('pflege.schedule.link_panel.contact_fallback', { id: c.id })).toString();
}

/** Empfänger für „Link an Kunden senden": Familienkonto-Person oder (ohne Konto) Kontakt des PB. */
interface Recipient {
  key: string;
  name: string;
  email: string;
}

interface Props {
  userId: number;
  /** Familienkonto des PB — dessen gespeicherte Personen mit E-Mail sind die Empfänger. */
  accountId?: number | null;
  dark: boolean;
  /** Wird mit dem aktuellen Sperr-Status aufgerufen (für den Speichern-Button). */
  onLockChange?: (locked: boolean) => void;
}

function fmtDateTime(iso: string | null): string {
  return formatDateTime(iso, '');
}

/**
 * Baut die öffentliche Kundenlink-URL aus dem Token. Bewusst NICHT die
 * `url` aus der API-Antwort: die hängt serverseitig von WEBAPP_URL/Config-Cache
 * ab und kann auf die falsche Domain zeigen. Das Admin-Panel und die öffentliche
 * Seite laufen auf derselben Origin (crm.senihelp24.de) — daher ist die Origin
 * hier immer korrekt.
 */
function publicLinkUrl(token: string): string {
  return `${window.location.origin}/tagesplan/${token}`;
}

export function ScheduleLinkPanel({ userId, accountId = null, dark, onLockChange }: Props) {
  const { t: tr } = useTranslation();
  const tc = getThemeColors(dark);
  const canEditLocked = usePermission('pb.schedule.edit_locked');

  const [link, setLink] = useState<PbScheduleLinkInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<'url' | 'pin' | null>(null);
  const [confirmRegen, setConfirmRegen] = useState(false);

  // „Link an Kunden senden" — gespeicherte Personen des Familienkontos mit
  // E-Mail (doppelte Adressen nur einmal).
  const [contacts, setContacts] = useState<Recipient[]>([]);
  const [selectedContactId, setSelectedContactId] = useState<string>('');
  const [sending, setSending] = useState(false);
  const [sendMsg, setSendMsg] = useState<{ text: string; ok: boolean } | null>(null);

  const apply = useCallback((info: PbScheduleLinkInfo | null) => {
    setLink(info);
    onLockChange?.(info?.locked ?? false);
  }, [onLockChange]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getPbScheduleLink(userId)
      .then(info => { if (alive) apply(info); })
      .catch(() => { if (alive) setError(tr('pflege.schedule.link_panel.load_error')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [userId, apply, tr]);

  const run = useCallback(async (fn: () => Promise<PbScheduleLinkInfo | null>) => {
    setBusy(true);
    setError(null);
    try {
      apply(await fn());
    } catch {
      setError(tr('pflege.schedule.link_panel.action_error'));
    } finally {
      setBusy(false);
      setConfirmRegen(false);
    }
  }, [apply, tr]);

  const handleCreate = () => run(() => createPbScheduleLink(userId));
  const handleRegenerate = () => run(() => createPbScheduleLink(userId));
  const handleUnlock = () => run(() => unlockPbSchedule(userId));
  const handleRevoke = () => run(async () => { await deletePbScheduleLink(userId); return null; });

  // Empfänger laden: nur die im Familienkonto tatsächlich gespeicherten
  // Personen mit E-Mail (Feedback 14.10.2026 — die alten CRM-1.0-
  // Ansprechpartner blähten die Liste auf). Die Ansprechpartner-Tabelle dient
  // nur noch als Fallback für PB ohne Familienkonto. Fehler lassen die Liste
  // einfach leer.
  useEffect(() => {
    let alive = true;
    const load: Promise<Recipient[]> = accountId != null
      ? v2FamilyAccounts.get(accountId)
          .then(acc => (acc.members ?? []).filter(m => m.email).map<Recipient>(m => ({
            key: `u:${m.user_id}`,
            name: m.fullname || `${m.vorname ?? ''} ${m.nachname ?? ''}`.trim() || String(m.email),
            email: String(m.email),
          })))
          .catch(() => [] as Recipient[])
      : v1Contacts.listForUser(userId, { perPage: 100 })
          .then(p => (p.data ?? []).filter(c => c.email).map<Recipient>(c => ({
            key: `c:${c.id}`, name: contactName(c, tr), email: String(c.email),
          })))
          .catch(() => [] as Recipient[]);
    load.then(list => {
      if (!alive) return;
      const seen = new Set<string>();
      setContacts(list.filter(r => {
        const k = r.email.toLowerCase();
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      }));
    });
    return () => { alive = false; };
  }, [userId, accountId, tr]);

  const handleSend = async () => {
    const c = contacts.find(x => x.key === selectedContactId);
    if (!c || !link) return;
    setSending(true);
    setSendMsg(null);
    try {
      await sendPbScheduleLink(userId, { email: c.email, name: c.name, url: publicLinkUrl(link.token) });
      setSendMsg({ text: tr('pflege.schedule.link_panel.send_success', { email: c.email }), ok: true });
    } catch (e) {
      const code = (e as { response?: { data?: { errors?: { code?: string }[] } } })?.response?.data?.errors?.[0]?.code;
      setSendMsg({
        text: code === 'E_RECIPIENT_UNKNOWN'
          ? tr('pflege.schedule.link_panel.send_error_recipient')
          : code === 'E_LINK_LOCKED'
            ? tr('pflege.schedule.link_panel.send_error_locked')
            : tr('pflege.schedule.link_panel.send_error'),
        ok: false,
      });
    } finally {
      setSending(false);
    }
  };

  const copy = async (kind: 'url' | 'pin', value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(kind);
      setTimeout(() => setCopied(c => (c === kind ? null : c)), 1500);
    } catch { /* clipboard nicht verfügbar — ignorieren */ }
  };

  const readonlyField = (label: string, value: string, kind: 'url' | 'pin') => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 200 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: tc.muted }}>{label}</span>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          readOnly
          value={value}
          onFocus={e => e.currentTarget.select()}
          style={{
            flex: 1, fontSize: kind === 'pin' ? 16 : 12.5, letterSpacing: kind === 'pin' ? '0.3em' : undefined,
            padding: '8px 10px', border: `1px solid ${tc.border}`, borderRadius: 8, background: tc.cardMuted,
            color: tc.text, fontFamily: kind === 'pin' ? 'monospace' : 'Outfit,sans-serif', outline: 'none',
            fontWeight: kind === 'pin' ? 700 : 400,
          }}
        />
        <Button dark={dark} variant="secondary" size="sm" onClick={() => copy(kind, value)}>
          {copied === kind ? '✓' : tr('common.action.copy')}
        </Button>
      </div>
    </div>
  );

  return (
    <Card dark={dark} style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, color: tc.heading, fontSize: 15, fontWeight: 700 }}>{tr('pflege.schedule.link_panel.title')}</h3>
        {link && (
          <Badge dark={dark} tone={link.locked ? 'neutral' : 'brand'}>
            {link.locked
              ? (link.locked_at
                ? tr('pflege.schedule.link_panel.locked_with_date', { date: fmtDateTime(link.locked_at) })
                : tr('pflege.schedule.link_panel.locked_plain'))
              : tr('pflege.schedule.link_panel.open_badge')}
          </Badge>
        )}
      </div>
      <p style={{ margin: 0, color: tc.muted, fontSize: 12.5, lineHeight: 1.5 }}>
        {tr('pflege.schedule.link_panel.description')}
      </p>

      {error && (
        <div role="alert" style={{ padding: '8px 10px', background: dark ? '#1a0505' : '#fff5f5', border: '1px solid #fda29b', borderRadius: 8, fontSize: 12, color: '#b42318' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ fontSize: 12.5, color: tc.muted }}>{tr('pflege.detail.setcard_view_loading')}</div>
      ) : !link ? (
        <div>
          <Button dark={dark} variant="primary" size="sm" disabled={busy} onClick={handleCreate}>
            {busy ? tr('pflege.schedule.link_panel.creating') : tr('pflege.schedule.link_panel.create_button')}
          </Button>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            {readonlyField(tr('pflege.schedule.link_panel.link_label'), publicLinkUrl(link.token), 'url')}
            {readonlyField(tr('pflege.schedule.link_panel.pin_label'), link.pin, 'pin')}
          </div>

          {/* Link an Kunden senden — Ansprechpartner auswählen */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 4, borderTop: `1px solid ${tc.borderSoft}` }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: tc.muted, marginTop: 6 }}>{tr('pflege.schedule.link_panel.send_to_customer')}</span>
            {contacts.length === 0 ? (
              <span style={{ fontSize: 12, color: tc.muted }}>
                {tr('pflege.schedule.link_panel.no_contact')}
              </span>
            ) : (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <select
                  aria-label={tr('betreuung.form.section.ansprechpartner.title')}
                  value={selectedContactId}
                  onChange={e => { setSelectedContactId(e.target.value); setSendMsg(null); }}
                  style={{ fontSize: 13, padding: '8px 10px', border: `1px solid ${tc.border}`, borderRadius: 8, background: tc.inputBg, color: tc.text, fontFamily: 'Outfit,sans-serif', outline: 'none', minWidth: 240 }}
                >
                  <option value="">{tr('pflege.schedule.link_panel.contact_select_placeholder')}</option>
                  {contacts.map(c => (
                    <option key={c.key} value={c.key}>{c.name} · {c.email}</option>
                  ))}
                </select>
                <Button dark={dark} variant="primary" size="sm" disabled={sending || !selectedContactId} onClick={handleSend}>
                  {sending ? tr('pflege.schedule.link_panel.sending') : tr('pflege.schedule.link_panel.send_to_customer')}
                </Button>
              </div>
            )}
            {sendMsg && <span style={{ fontSize: 12, color: sendMsg.ok ? '#027a48' : '#b42318', fontWeight: 600 }}>{sendMsg.text}</span>}
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {link.locked && canEditLocked && (
              <Button dark={dark} variant="primary" size="sm" disabled={busy} onClick={handleUnlock}>
                {tr('pflege.schedule.link_panel.unlock_button')}
              </Button>
            )}
            {link.locked && !canEditLocked && (
              <span style={{ fontSize: 11.5, color: tc.muted }}>
                {tr('pflege.schedule.link_panel.unlock_permission_hint')}
              </span>
            )}

            {!confirmRegen ? (
              <Button dark={dark} variant="secondary" size="sm" disabled={busy} onClick={() => setConfirmRegen(true)}>
                {tr('pflege.schedule.link_panel.regenerate_button')}
              </Button>
            ) : (
              <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 12, color: tc.text }}>
                {tr('pflege.schedule.link_panel.regenerate_confirm')}
                <Button dark={dark} variant="primary" size="sm" disabled={busy} onClick={handleRegenerate}>{tr('pflege.schedule.link_panel.regenerate_confirm_yes')}</Button>
                <Button dark={dark} variant="secondary" size="sm" disabled={busy} onClick={() => setConfirmRegen(false)}>{tr('common.action.cancel')}</Button>
              </span>
            )}

            <button
              type="button"
              onClick={handleRevoke}
              disabled={busy}
              style={{ fontSize: 12, padding: '7px 12px', background: dark ? '#1a0505' : '#fff5f5', border: '1px solid #fecdca', borderRadius: 7, color: '#b42318', cursor: busy ? 'not-allowed' : 'pointer', fontFamily: 'Outfit,sans-serif' }}
            >
              {tr('pflege.schedule.link_panel.revoke_button')}
            </button>
          </div>
          {link.last_accessed_at && (
            <div style={{ fontSize: 11, color: tc.muted }}>{tr('pflege.schedule.link_panel.last_accessed', { date: fmtDateTime(link.last_accessed_at) })}</div>
          )}
        </>
      )}
    </Card>
  );
}
